import React, { useState } from "react";
import { Table, Button, Badge, Form } from "react-bootstrap";
import { FaChevronRight, FaChevronDown } from "react-icons/fa";

/* ================= styles ================= */

const compactCell = {
  padding: "1px 4px",
  verticalAlign: "middle",
  whiteSpace: "nowrap",
};

const stickyHeader = {
  position: "sticky",
  top: 0,
  background: "#f8f9fa",
  zIndex: 2,
};

const diffStyle = {
  color: "#d10000",
};

/* ================= helpers ================= */

const isStrDiff = (a, b) =>
  (a || "").trim().toUpperCase() !== (b || "").trim().toUpperCase();

const isDateDiff = (a, b) => (a || "") !== (b || "");

const isNumDiffTol = (a, b, tol) =>
  Math.abs(Number(a || 0) - Number(b || 0)) > tol;

const f2 = (v) => Number(v || 0).toFixed(2);

const badgeColor = (type) => {
  const t = type?.toUpperCase() || "";
  if (t === "EXACT") return "success";
  if (t.includes("GROUP")) return "primary";
  if (t.includes("FUZZY") || t.includes("RELAXED")) return "warning";
  if (t.includes("ONLY IN GSTR")) return "danger";
  if (t.includes("ONLY IN ERP")) return "secondary";
  return "dark";
};

/* ================= component ================= */

const MatchResultsGrid = ({
  data = [],
  onApprove,
  onUnapprove,
  onUnmatch,
  onOpenGroup,
  onBulkApprove,
  onBulkUnapprove,
  onBulkUnmatch,
}) => {
  const [expanded, setExpanded] = useState({});
  const [selected, setSelected] = useState({});
  const [filterMode, setFilterMode] = useState("ALL"); // ALL | DIFF | MATCHED
  const [tolerance, setTolerance] = useState(0); // ₹0–₹10

  /* ---------- selection ---------- */

  const allSelected =
    data.length > 0 && data.every((_, i) => selected[i]);

  const selectedCount = Object.values(selected).filter(Boolean).length;

  const toggleSelectAll = (checked) => {
    const next = {};
    if (checked) data.forEach((_, i) => (next[i] = true));
    setSelected(next);
  };

  const toggleRowSelect = (idx) =>
    setSelected((p) => ({ ...p, [idx]: !p[idx] }));

  const toggleExpand = (idx) =>
    setExpanded((p) => ({ ...p, [idx]: !p[idx] }));

  /* ---------- filtering + tolerance ---------- */

  const filteredData = data.filter((row) => {
    const partyDiff = isStrDiff(row.PARTY_NAME_2B, row.PARTY_NAME_ERP);
    const billNoDiff = isStrDiff(row.BILL_NO_2B, row.BILL_NO_ERP);
    const billDtDiff = isDateDiff(row.BILL_DATE_2B, row.BILL_DATE_ERP);

    const taxableDiff = isNumDiffTol(row.TAXABLE_2B, row.ASSESSABLE_ERP, tolerance);
    const igstDiff = isNumDiffTol(row.IGST_2B, row.IGST_ERP, tolerance);
    const cgstDiff = isNumDiffTol(row.CGST_2B, row.CGST_ERP, tolerance);
    const sgstDiff = isNumDiffTol(row.SGST_2B, row.SGST_ERP, tolerance);
    const cessDiff = isNumDiffTol(row.CESS_2B, row.CESS_ERP, tolerance);

    const hasDiff =
      partyDiff ||
      billNoDiff ||
      billDtDiff ||
      taxableDiff ||
      igstDiff ||
      cgstDiff ||
      sgstDiff ||
      cessDiff;

    if (filterMode === "DIFF") return hasDiff;
    if (filterMode === "MATCHED") return !hasDiff;
    return true;
  });

  const selectedRows = data.filter((_, i) => selected[i]);

  const invalidSelected = selectedRows.some(
    (r) =>
      r.MATCH_TYPE === "ONLY IN ERP" ||
      r.MATCH_TYPE === "ONLY IN GSTR"
  );
  
  /* ================= render ================= */

  return (
    <>
      {/* ===== Unified Control Bar ===== */}
      <div
        style={{
          marginBottom: "4px",
          padding: "3px 6px",
          border: "1px solid #ddd",
          borderRadius: "4px",
          background: "#f8f9fa",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          fontSize: "0.62rem",
        }}
      >
        {/* LEFT */}
        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <Form.Check
            type="checkbox"
            checked={allSelected}
            onChange={(e) => toggleSelectAll(e.target.checked)}
            label="All"
          />

          {selectedCount > 0 && <b>{selectedCount} selected</b>}

          <Button
            size="sm"
            variant="success"
            disabled={selectedCount === 0 || invalidSelected}
            style={{ fontSize: "0.6rem", padding: "1px 6px" }}
            onClick={() => onBulkApprove(selectedRows)}
          >
            Approve
          </Button>
          <Button
            size="sm"
            variant="danger"
            disabled={selectedCount === 0 || invalidSelected}
            style={{ fontSize: "0.6rem", padding: "1px 6px" }}
            onClick={() => onBulkUnapprove(selectedRows)}
          >
            Unapprove
          </Button>

          <Button
            size="sm"
            variant="warning"
            disabled={selectedCount === 0}
            style={{ fontSize: "0.6rem", padding: "1px 6px" }}
            onClick={() =>onBulkUnmatch(selectedRows)}
          >
            Unmatch
          </Button>
        </div>

        {/* RIGHT */}
        <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
          <span>Tol:</span>
          <Button
            size="sm"
            variant="outline-secondary"
            style={{ fontSize: "0.6rem", padding: "0 4px" }}
            onClick={() => setTolerance((t) => Math.max(0, t - 1))}
          >
            ←
          </Button>

          <span style={{ minWidth: "12px", textAlign: "center" }}>
            {tolerance}
          </span>

          <Button
            size="sm"
            variant="outline-secondary"
            style={{ fontSize: "0.6rem", padding: "0 4px" }}
            onClick={() => setTolerance((t) => Math.min(10, t + 1))}
          >
            →
          </Button>

          <Button
            size="sm"
            variant={filterMode === "ALL" ? "secondary" : "outline-secondary"}
            style={{ fontSize: "0.6rem", padding: "1px 6px" }}
            onClick={() => setFilterMode("ALL")}
          >
            All
          </Button>

          <Button
            size="sm"
            variant={filterMode === "DIFF" ? "danger" : "outline-danger"}
            style={{ fontSize: "0.6rem", padding: "1px 6px" }}
            onClick={() => setFilterMode("DIFF")}
          >
            Diff
          </Button>

          <Button
            size="sm"
            variant={filterMode === "MATCHED" ? "success" : "outline-success"}
            style={{ fontSize: "0.6rem", padding: "1px 6px" }}
            onClick={() => setFilterMode("MATCHED")}
          >
            Matched
          </Button>
        </div>
      </div>

      {/* ===== Grid ===== */}
      <div
        style={{
          maxHeight: "380px",
          overflow: "auto",
          border: "1px solid #ddd",
          borderRadius: "4px",
        }}
      >
        <Table bordered hover size="sm" style={{ fontSize: "0.62rem", margin: 0 }}>
          <thead>
            <tr>
              <th style={{ ...compactCell, ...stickyHeader }}></th>
              <th style={{ ...compactCell, ...stickyHeader }}>Type</th>
              <th style={{ ...compactCell, ...stickyHeader }}>GSTIN</th>
              <th style={{ ...compactCell, ...stickyHeader }}>2B Party</th>
              <th style={{ ...compactCell, ...stickyHeader }}>ERP Party</th>
              <th style={{ ...compactCell, ...stickyHeader }}>2B Inv</th>
              <th style={{ ...compactCell, ...stickyHeader }}>ERP Inv</th>
              <th style={{ ...compactCell, ...stickyHeader }}>2B Dt</th>
              <th style={{ ...compactCell, ...stickyHeader }}>ERP Dt</th>
              <th className="text-end" style={{ ...compactCell, ...stickyHeader }}>Taxable 2B</th>
              <th className="text-end" style={{ ...compactCell, ...stickyHeader }}>Assess ERP</th>
              <th className="text-end" style={{ ...compactCell, ...stickyHeader }}>IGST 2B</th>
              <th className="text-end" style={{ ...compactCell, ...stickyHeader }}>IGST ERP</th>
              <th className="text-end" style={{ ...compactCell, ...stickyHeader }}>CGST 2B</th>
              <th className="text-end" style={{ ...compactCell, ...stickyHeader }}>CGST ERP</th>
              <th className="text-end" style={{ ...compactCell, ...stickyHeader }}>SGST 2B</th>
              <th className="text-end" style={{ ...compactCell, ...stickyHeader }}>SGST ERP</th>
              <th className="text-end" style={{ ...compactCell, ...stickyHeader }}>CESS 2B</th>
              <th className="text-end" style={{ ...compactCell, ...stickyHeader }}>CESS ERP</th>
              <th style={{ ...compactCell, ...stickyHeader }}>Score</th>
              <th style={{ ...compactCell, ...stickyHeader }}>Action</th>
            </tr>
          </thead>

          <tbody>
            {filteredData.map((row, idx) => {
              const partyDiff = isStrDiff(row.PARTY_NAME_2B, row.PARTY_NAME_ERP);
              const billNoDiff = isStrDiff(row.BILL_NO_2B, row.BILL_NO_ERP);
              const billDtDiff = isDateDiff(row.BILL_DATE_2B, row.BILL_DATE_ERP);

              const taxableDiff = isNumDiffTol(row.TAXABLE_2B, row.ASSESSABLE_ERP, tolerance);
              const igstDiff = isNumDiffTol(row.IGST_2B, row.IGST_ERP, tolerance);
              const cgstDiff = isNumDiffTol(row.CGST_2B, row.CGST_ERP, tolerance);
              const sgstDiff = isNumDiffTol(row.SGST_2B, row.SGST_ERP, tolerance);
              const cessDiff = isNumDiffTol(row.CESS_2B, row.CESS_ERP, tolerance);

              return (
                <tr
                  key={idx}
                  style={{
                    height: "20px",
                    opacity: row.APPROVED === "Y" ? 0.45 : 1,
                  }}
                >
                  <td style={compactCell}>
                    <Form.Check
                      type="checkbox"
                      checked={!!selected[idx]}
                      onChange={() => toggleRowSelect(idx)}
                    />
                  </td>
                  <td style={compactCell}>
                    <Badge bg={badgeColor(row.MATCH_TYPE)} style={{ fontSize: "0.6rem" }}>
                      {row.MATCH_TYPE}
                    </Badge>
                  </td>

                  <td style={compactCell}>{row.GSTIN}</td>

                  <td style={{ ...compactCell, ...(partyDiff ? diffStyle : {}) }}>
                    {row.PARTY_NAME_2B}
                  </td>
                  <td style={{ ...compactCell, ...(partyDiff ? diffStyle : {}) }}>
                    {row.PARTY_NAME_ERP}
                  </td>

                  <td style={{ ...compactCell, ...(billNoDiff ? diffStyle : {}) }}>
                    {row.BILL_NO_2B}
                  </td>
                  <td style={{ ...compactCell, ...(billNoDiff ? diffStyle : {}) }}>
                    {row.BILL_NO_ERP}
                  </td>

                  <td style={{ ...compactCell, ...(billDtDiff ? diffStyle : {}) }}>
                    {row.BILL_DATE_2B}
                  </td>
                  <td style={{ ...compactCell, ...(billDtDiff ? diffStyle : {}) }}>
                    {row.BILL_DATE_ERP}
                  </td>

                  <td className="text-end" style={{ ...compactCell, ...(taxableDiff ? diffStyle : {}) }}>
                    {f2(row.TAXABLE_2B)}
                  </td>
                  <td className="text-end" style={{ ...compactCell, ...(taxableDiff ? diffStyle : {}) }}>
                    {f2(row.ASSESSABLE_ERP)}
                  </td>

                  <td className="text-end" style={{ ...compactCell, ...(igstDiff ? diffStyle : {}) }}>
                    {f2(row.IGST_2B)}
                  </td>
                  <td className="text-end" style={{ ...compactCell, ...(igstDiff ? diffStyle : {}) }}>
                    {f2(row.IGST_ERP)}
                  </td>

                  <td className="text-end" style={{ ...compactCell, ...(cgstDiff ? diffStyle : {}) }}>
                    {f2(row.CGST_2B)}
                  </td>
                  <td className="text-end" style={{ ...compactCell, ...(cgstDiff ? diffStyle : {}) }}>
                    {f2(row.CGST_ERP)}
                  </td>

                  <td className="text-end" style={{ ...compactCell, ...(sgstDiff ? diffStyle : {}) }}>
                    {f2(row.SGST_2B)}
                  </td>
                  <td className="text-end" style={{ ...compactCell, ...(sgstDiff ? diffStyle : {}) }}>
                    {f2(row.SGST_ERP)}
                  </td>

                  <td className="text-end" style={{ ...compactCell, ...(cessDiff ? diffStyle : {}) }}>
                    {f2(row.CESS_2B)}
                  </td>
                  <td className="text-end" style={{ ...compactCell, ...(cessDiff ? diffStyle : {}) }}>
                    {f2(row.CESS_ERP)}
                  </td>

                  <td style={compactCell}>
                    <Badge bg="info" style={{ fontSize: "0.6rem" }}>
                      {row.MATCH_SCORE}%
                    </Badge>
                  </td>

                  <td style={compactCell}>
                    {/* APPROVE (only when NOT approved) */}
                    {row.APPROVED === "N" && (
                      <Button
                        size="sm"
                        variant="success"
                        style={{
                          fontSize: "0.6rem",
                          padding: "1px 6px",
                        }}
                        onClick={() => onApprove(row)}
                      >
                        Approve
                      </Button>
                    )}

                    {/* UNAPPROVE (only when already approved) */}
                    {row.APPROVED === "Y" && (
                      <Button
                        size="sm"
                        variant="danger"
                        style={{
                          fontSize: "0.6rem",
                          padding: "1px 6px",
                        }}
                        onClick={() => onUnapprove(row)}
                      >
                        Unapprove
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      style={{ fontSize: "0.6rem", padding: "1px 6px", marginLeft: "2px" }}
                      onClick={() => onUnmatch(row)}
                    >
                      Unmatch
                    </Button>

                    {row.MATCH_TYPE?.includes("GROUP") && (
                      <Button
                        size="sm"
                        variant="outline-primary"
                        style={{ fontSize: "0.6rem", padding: "1px 6px", marginLeft: "2px" }}
                        onClick={() => onOpenGroup(row)}
                      >
                        View
                      </Button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </div>
    </>
  );
};

export default MatchResultsGrid;
